import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "The full details before they go public",
  "First invitation to apply when the doors open this fall",
  "Find out first about new experiences we're adding to the spring retreat — plus a surprise bonus for the women committed to their reinvention and aliveness",
];

function PriorityForm() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error("Please add your first name."); return; }
    if (!form.email.includes("@")) { toast.error("Please add a valid email."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/priority/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      toast.error("Something didn't go through — please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="priority-success" className="text-center py-4">
        <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-[#5C6B50]/12">
          <Check className="w-6 h-6 text-[#5C6B50]" />
        </span>
        <h3 className="mt-4 font-serif text-2xl tracking-tight text-[#2C2A28]">You're on the list.</h3>
        <p className="mt-3 text-[#595550] leading-relaxed text-sm max-w-sm mx-auto">
          You'll be the first to hear when applications for the retreat — April 25 to May 2, 2027 — open this fall,
          before anyone else, with the full details and your place held for you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">What the list gives you</p>
      <ul className="mt-3 space-y-2.5">
        {PERKS.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-[15px] text-[#3a3632] leading-snug">
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#C9A24B]" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 space-y-3">
        <input
          data-testid="priority-first-name"
          type="text"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          placeholder="First name"
          className="w-full bg-white/80 border hairline rounded-sm px-4 py-3.5 outline-none focus:border-[#B85C38] text-[#2C2A28]"
        />
        <input
          data-testid="priority-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@email.com"
          className="w-full bg-white/80 border hairline rounded-sm px-4 py-3.5 outline-none focus:border-[#B85C38] text-[#2C2A28]"
        />
      </div>
      <button
        data-testid="priority-submit"
        type="submit"
        disabled={loading}
        className="mt-4 w-full bg-[#B85C38] text-[#F7F5F0] rounded-full py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? "Holding your place…" : <>Claim my place on the list <ArrowRight className="w-4 h-4" /></>}
      </button>
      <p className="mt-3 text-center text-xs text-[#7a746c] italic">For the women ready to walk through the door.</p>
    </form>
  );
}

export default function PriorityList() {
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C2A28] antialiased flex flex-col">
      <header className="px-6 sm:px-10 py-5">
        <a href="/" className="font-mono-a text-[10px] uppercase tracking-[0.28em] text-[#9a938a] hover:text-[#B85C38] transition-colors" data-testid="priority-home-link">
          The Ageless Woman
        </a>
      </header>

      <main className="flex-1 flex items-center px-6 sm:px-10 pb-10">
        <div className="w-full max-w-5xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          {/* LEFT — desire + scarcity */}
          <div>
            <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#B85C38]" data-testid="priority-eyebrow">
              The Ageless Woman · April 25 – May 2, 2027
            </p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.03]">
              Keeping la dolce vita alive — together.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#595550] leading-relaxed">
              The retreat takes place in Florence and Tuscany, April 25 to May 2, 2027.
              Applications open this fall — and because the circle is small, the priority list is
              invited first.
            </p>
            <p className="mt-4 text-[15px] sm:text-base text-[#595550] leading-relaxed">
              There's nothing to pay and nothing to apply for right now. Add your name, and when the
              doors open you'll be the very first we reach out to — for the women who are done letting
              their vitality leak away, and ready to bring more energy into life, sweetly, on purpose.
            </p>
            <p className="mt-4 text-[15px] sm:text-base text-[#2C2A28] leading-relaxed">
              We're keeping the circle small — so we open the seats to this list
              <span className="text-[#B85C38]"> before anyone else.</span>
            </p>
          </div>

          {/* RIGHT — the door */}
          <div className="bg-white/60 border hairline rounded-md px-6 sm:px-8 py-8 shadow-[0_20px_60px_-40px_rgba(60,45,35,0.6)]">
            <PriorityForm />
          </div>
        </div>
      </main>
    </div>
  );
}
